import { prefersReducedMotion } from '@theme/utilities';

const SLIDER_SELECTOR = '[data-group-slider]';
const TRACK_SELECTOR = '[data-group-slider-track]';
const CONTROLS_SELECTOR = '[data-group-slider-controls]';
const PREVIOUS_SELECTOR = '[data-group-slider-previous]';
const NEXT_SELECTOR = '[data-group-slider-next]';
const SCROLL_UPDATE_INTERVAL = 120;
const EDGE_TOLERANCE = 0.5;

class GroupSlider {
  /** @type {HTMLElement} */
  root;
  /** @type {HTMLElement | null} */
  track;
  /** @type {HTMLButtonElement | null} */
  previous;
  /** @type {HTMLButtonElement | null} */
  next;
  /** @type {ResizeObserver | undefined} */
  resizeObserver;
  /** @type {() => void} */
  onScroll;
  /** @type {() => void} */
  handlePrevious;
  /** @type {() => void} */
  handleNext;
  /** @type {number | null} */
  scrollRaf = null;
  /** @type {number | null} */
  scrollTimeout = null;

  /** @type {boolean} */
  initialised = false;

  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.track = root.querySelector(TRACK_SELECTOR);

    if (!this.track) return;

    const controls = root.querySelector(CONTROLS_SELECTOR);
    this.previous = controls?.querySelector(PREVIOUS_SELECTOR) ?? null;
    this.next = controls?.querySelector(NEXT_SELECTOR) ?? null;

    this.handlePrevious = () => this.scroll(-1);
    this.handleNext = () => this.scroll(1);
    this.onScroll = () => this.requestUpdate();

    this.previous?.addEventListener('click', this.handlePrevious);
    this.next?.addEventListener('click', this.handleNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.requestUpdate(true));
    this.resizeObserver.observe(this.track);

    this.initialised = true;
    queueMicrotask(() => this.requestUpdate(true));
  }

  /**
   * @returns {number}
   */
  get gap() {
    const track = this.track;
    if (!track) return 0;

    const { gap, columnGap } = getComputedStyle(track);
    const value = parseFloat(columnGap || gap || '0');

    return Number.isNaN(value) ? 0 : value;
  }

  /**
   * @returns {boolean}
   */
  get hasOverflow() {
    if (!this.track) return false;

    return this.track.scrollWidth - this.track.clientWidth > 1;
  }

  /**
   * @returns {number}
   */
  getScrollAmount() {
    if (!this.track) return 0;

    const viewportWidth = this.track.clientWidth;
    const firstChild = /** @type {HTMLElement | null} */ (this.track.querySelector(':scope > *'));

    if (!firstChild) return viewportWidth;

    const childWidth = firstChild.getBoundingClientRect().width;

    return Math.max(viewportWidth, childWidth + this.gap);
  }

  /**
   * @param {number} direction
   */
  scroll(direction) {
    if (!this.track) return;

    const amount = this.getScrollAmount() * direction;

    this.track.scrollBy({
      left: amount,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });

    this.requestUpdate();
  }

  update() {
    if (!this.track) return;

    const track = this.track;
    if (!track) return;

    const { scrollWidth, clientWidth, scrollLeft } = track;
    const maxScroll = Math.max(scrollWidth - clientWidth, 0);
    const hasOverflow = maxScroll > EDGE_TOLERANCE;
    const remainingScroll = Math.max(maxScroll - scrollLeft, 0);

    this.root.classList.toggle('is-scrollable', hasOverflow);

    if (this.previous) {
      const disablePrevious = !hasOverflow || scrollLeft <= EDGE_TOLERANCE;
      this.previous.disabled = disablePrevious;
      this.previous.classList.toggle('group-block__slider-arrow--disabled', disablePrevious);
    }

    if (this.next) {
      const disableNext = !hasOverflow || remainingScroll <= EDGE_TOLERANCE;
      this.next.disabled = disableNext;
      this.next.classList.toggle('group-block__slider-arrow--disabled', disableNext);
    }
  }

  /**
   * @param {boolean} [immediate=false]
   */
  requestUpdate(immediate = false) {
    if (immediate) {
      this.cancelScheduledUpdates();
      this.update();
      return;
    }

    if (this.scrollRaf === null) {
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.update();
      });
    }

    if (this.scrollTimeout !== null) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = window.setTimeout(() => {
      this.scrollTimeout = null;
      this.update();
    }, SCROLL_UPDATE_INTERVAL);
  }

  cancelScheduledUpdates() {
    if (this.scrollRaf !== null) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }

    if (this.scrollTimeout !== null) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
  }

  destroy() {
    if (!this.initialised || !this.track) return;

    this.previous?.removeEventListener('click', this.handlePrevious);
    this.next?.removeEventListener('click', this.handleNext);
    this.track.removeEventListener('scroll', this.onScroll);
    this.cancelScheduledUpdates();

    this.resizeObserver?.disconnect();
    this.initialised = false;
  }
}

const instances = new WeakMap();

/**
 * @param {ParentNode} [context=document]
 */
function initSliders(context = document) {
  const sliders = context.querySelectorAll(SLIDER_SELECTOR);

  sliders.forEach((slider) => {
    if (instances.has(slider)) return;

    if (!(slider instanceof HTMLElement)) return;

    const instance = new GroupSlider(slider);

    if (instance.initialised) {
      instances.set(slider, instance);
    }
  });
}

/**
 * @param {ParentNode} [context=document]
 */
function destroySliders(context = document) {
  const sliders = context.querySelectorAll(SLIDER_SELECTOR);

  sliders.forEach((slider) => {
    const instance = instances.get(slider);
    instance?.destroy();
    instances.delete(slider);
  });
}

document.addEventListener('DOMContentLoaded', () => initSliders());

document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {ParentNode} */ (event.target);
  initSliders(target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const target = /** @type {ParentNode} */ (event.target);
  destroySliders(target);
});

document.addEventListener('shopify:block:load', (event) => {
  const target = /** @type {ParentNode} */ (event.target);
  initSliders(target);
});

document.addEventListener('shopify:block:unload', (event) => {
  const target = /** @type {ParentNode} */ (event.target);
  destroySliders(target);
});

